import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, User, Building2, Globe, FolderOpen, Settings, FileText, MessageSquare, Plus, LogIn, UserPlus, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTrustNGOSession } from "@/hooks/useTrustNGOSession";
import { useRole } from "@/hooks/useRole";
import { signUpWithTrustNGO } from "@/lib/trustngo-auth";

const services = [
  { label: "ISO Certifications", path: "/services/iso-certifications" },
  { label: "CSR & Impact Assessment", path: "/services/csr-impact" },
  { label: "Skill Development", path: "/services/skill-development" },
  { label: "Research & Surveys", path: "/services/research-surveys" },
  { label: "Management Consulting", path: "/services/management-consulting" },
];

const aiTools = [
  { label: "AI Compliance Advisor", path: "/tools/compliance-advisor" },
  { label: "Certification Readiness Checker", path: "/tools/readiness-checker" },
  { label: "CSR Project Idea Generator", path: "/tools/csr-generator" },
  { label: "Impact Assessment Estimator", path: "/tools/impact-estimator" },
];

const platforms = [
  { label: "TrustNGO Hub", code: "trustngo" },
  { label: "SaarthiAI", code: "saarthi" },
  { label: "SigmaCert", code: "sigmacert" },
];

const workspaceLinks = [
  { label: "Dashboard", path: "/workspace", icon: FolderOpen },
  { label: "My Projects", path: "/workspace/projects", icon: FolderOpen },
  { label: "AI Tools", path: "/workspace/tools", icon: MessageSquare },
  { label: "Documents", path: "/workspace/documents", icon: FileText },
  { label: "Settings", path: "/workspace/settings", icon: Settings },
];

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Services", path: "/services", children: services },
  { label: "Partnerships", path: "/partnerships" },
  { label: "AI Tools", path: "/tools", children: aiTools },
  { label: "Clients & Projects", path: "/clients" },
  { label: "Resources", path: "/resources" },
  { label: "Schedule Meeting", path: "/schedule" },
  { label: "Contact Us", path: "/contact" },
];

function openPlatform(platformCode: string) {
  // For now, just redirect to the platform's main site
  // In production, this would use session-based routing
  const platformUrls: { [key: string]: string } = {
    trustngo: "https://trustngo.lovable.app",
    saarthi: "https://getsaarthi.com/",
    sigmacert: window.location.origin
  };
  window.location.href = platformUrls[platformCode] || window.location.origin;
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [platformDropdown, setPlatformDropdown] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [workspaceDropdown, setWorkspaceDropdown] = useState(false);
  const location = useLocation();
  const { user, session, logout } = useTrustNGOSession();
  const { isAdmin } = useRole();

  const authenticated = !!user && !!session;

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur-md">
      {/* TrustNGO Ecosystem Bar */}
      <div className="bg-primary/10 border-b border-border">
        <div className="container-narrow flex items-center justify-between px-4 py-1.5 md:px-8">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Globe className="h-3 w-3" />
            <span className="font-medium">Part of the TrustNGO Labs Growth Ecosystem.</span>
            <span className="text-muted-foreground/60">-</span>
            <span>SigmaCert Platform</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Platform Switcher */}
            <div
              className="relative"
              onMouseEnter={() => setPlatformDropdown(true)}
              onMouseLeave={() => setPlatformDropdown(false)}
            >
              <button className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                Switch Platform <ChevronDown className="h-3 w-3" />
              </button>
              {platformDropdown && (
                <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-card p-1 shadow-lg">
                  {platforms.map((p) => (
                    <button
                      key={p.code}
                      onClick={() => openPlatform(p.code)}
                      className={`block w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-secondary ${
                        p.code === "sigmacert" ? "text-accent font-medium" : "text-foreground"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User Info */}
            {authenticated && user && (
              <div className="flex items-center gap-2">
                {/* Workspace Dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setWorkspaceDropdown(true)}
                  onMouseLeave={() => setWorkspaceDropdown(false)}
                >
                  <Link
                    to="/workspace"
                    className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  >
                    <FolderOpen className="h-3 w-3" />
                    <span className="hidden sm:inline">Workspace</span>
                    <ChevronDown className="h-3 w-3" />
                  </Link>
                  {workspaceDropdown && (
                    <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-card p-1 shadow-lg">
                      {workspaceLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary"
                        >
                          <link.icon className="h-4 w-4" />
                          {link.label}
                        </Link>
                      ))}
                      {!isAdmin && (
                        <>
                          <div className="my-1 border-t border-border"></div>
                          <Link
                            to="/workspace/requests"
                            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-accent hover:bg-accent/10"
                          >
                            <Plus className="h-4 w-4" />
                            New Service Request
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* User Dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setUserDropdown(true)}
                  onMouseLeave={() => setUserDropdown(false)}
                >
                  <button className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                    <User className="h-3 w-3" />
                    <span className="hidden sm:inline">{user.user_metadata?.full_name || user.email?.split('@')[0] || "User"}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  {userDropdown && (
                    <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-card p-3 shadow-lg">
                      <div className="mb-2 border-b border-border pb-2">
                        <p className="text-sm font-medium text-foreground">
                          {user.user_metadata?.full_name || user.email?.split('@')[0] || "User"}
                        </p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                        {session?.tenants?.includes('sigmacert') && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                            <Building2 className="h-3 w-3" />
                            SigmaCert Platform
                          </div>
                        )}
                        {session?.roles?.['sigmacert'] && (
                          <span className="mt-1 inline-block rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent capitalize">
                            {session.roles['sigmacert']}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Link
                          to="/workspace/settings"
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-secondary transition-colors"
                        >
                          <Settings className="h-3.5 w-3.5" />
                          Account Settings
                        </Link>
                        <button
                          onClick={logout}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <LogOut className="h-3.5 w-3.5" />
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container-narrow flex items-center justify-between px-4 py-3 md:px-8">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <span className="font-heading text-lg font-bold text-primary-foreground">S</span>
          </div>
          <div>
            <span className="font-heading text-lg font-bold text-foreground">SigmaCert</span>
            <span className="block text-[10px] leading-none text-muted-foreground">AI-Enabled Consulting</span>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <div
              key={link.label}
              className="relative"
              onMouseEnter={() => link.children && setOpenDropdown(link.label)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                to={link.path}
                className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary ${
                  location.pathname === link.path || location.pathname.startsWith(link.path + "/")
                    ? "text-accent"
                    : "text-foreground"
                }`}
              >
                {link.label}
                {link.children && <ChevronDown className="h-3 w-3" />}
              </Link>
              {link.children && openDropdown === link.label && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-card p-2 shadow-lg"
                >
                  {link.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      className="block rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary"
                    >
                      {child.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </div>
          ))}

          {/* Workspace Navigation for Authenticated Users */}
          {authenticated && (
            <div
              className="relative ml-4 border-l border-border pl-4"
              onMouseEnter={() => setWorkspaceDropdown(true)}
              onMouseLeave={() => setWorkspaceDropdown(false)}
            >
              <Link
                to="/workspace"
                className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary ${
                  location.pathname.startsWith("/workspace")
                    ? "text-accent"
                    : "text-foreground"
                }`}
              >
                <FolderOpen className="h-4 w-4" />
                Workspace
                <ChevronDown className="h-3 w-3" />
              </Link>
              {workspaceDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-card p-2 shadow-lg"
                >
                  {workspaceLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary"
                    >
                      <link.icon className="h-4 w-4" />
                      {link.label}
                    </Link>
                  ))}
                  {!isAdmin && (
                    <>
                      <div className="my-1 border-t border-border"></div>
                      <Link
                        to="/workspace/requests"
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-accent hover:bg-accent/10"
                      >
                        <Plus className="h-4 w-4" />
                        New Service Request
                      </Link>
                    </>
                  )}
                </motion.div>
              )}
            </div>
          )}
        </nav>

        {authenticated ? (
          <Link
            to="/workspace/requests"
            className="hidden rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:opacity-90 lg:block"
          >
            <Plus className="mr-2 inline h-4 w-4" />
            New Request
          </Link>
        ) : (
          <div className="hidden items-center gap-2 lg:flex">
            <Link
              to="/workspace"
              className="inline-flex items-center rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Client Login
            </Link>
            <button
              type="button"
              onClick={signUpWithTrustNGO}
              className="inline-flex items-center rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:opacity-90"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Sign Up
            </button>
          </div>
        )}

        <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden">
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            className="overflow-hidden border-t border-border bg-card lg:hidden"
          >
            <nav className="flex flex-col gap-1 p-4">
              {navLinks.map((link) => (
                <div key={link.label}>
                  <Link
                    to={link.path}
                    onClick={() => !link.children && setMobileOpen(false)}
                    className="block rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                  >
                    {link.label}
                  </Link>
                  {link.children?.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => setMobileOpen(false)}
                      className="block rounded-md px-6 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ))}

              {/* Workspace Links for Mobile */}
              {authenticated && (
                <div className="mt-4 border-t border-border pt-4">
                  <p className="px-3 py-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Workspace</p>
                  {workspaceLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                    >
                      <link.icon className="h-4 w-4" />
                      {link.label}
                    </Link>
                  ))}
                  {!isAdmin && (
                    <Link
                      to="/workspace/requests"
                      onClick={() => setMobileOpen(false)}
                      className="mt-2 flex items-center gap-2 rounded-md bg-accent/10 px-3 py-2 text-sm font-medium text-accent"
                    >
                      <Plus className="h-4 w-4" />
                      New Service Request
                    </Link>
                  )}
                </div>
              )}

              {authenticated ? (
                <button
                  onClick={logout}
                  className="mt-2 flex items-center justify-center gap-2 rounded-lg border border-destructive/30 px-4 py-2 text-sm font-medium text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              ) : (
                <div className="mt-4 space-y-2 border-t border-border pt-4">
                  <Link
                    to="/workspace"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground"
                  >
                    <LogIn className="h-4 w-4" />
                    Client Login
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      signUpWithTrustNGO();
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground"
                  >
                    <UserPlus className="h-4 w-4" />
                    Sign Up with TrustNGO SSO
                  </button>
                  <Link
                    to="/admin/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Admin Login
                  </Link>
                </div>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
